<template>
  <section>
    <section id="canvas" />
    <div id="action-panel" class="white-panel">
      <a class="button is-small is-abb" @click="dissalowCeiling()">
        Disallow Ceiling
      </a>
      <a class="button is-small is-abb" @click="clearSelection()">
        Clear Selection
      </a>
      <a class="button is-small is-abb" @click="resetCamera()">
        Reset Camera
      </a>
      <a class="button is-small"
         :class="{'is-dark': !heuristics, 'is-abb': heuristics}"
        @click="heuristics = !heuristics">
        Heuristics {{ heuristics ? "On" : "Off"}}
      </a>
      <p><code>Click</code> to add/remove devices (3 max)</p>
      <p><code>Shift + Click</code> to add/remove obstructions</p>
      <p><code>Click + Drag</code> to Orbit (click outside volume for better control)</p>
      <p><code>Shift + Click + Drag</code> to Pan</p>
      <p><code>Scroll</code> to Zoom</p>
    </div>
    <div id="info-panel" class="white-panel" v-if="debugMode">
      Mouse X: {{ mouse.x | round }} <br/>
      Mouse Y: {{ mouse.y  | round }}<br/>
      Canvas Width: {{ canvasWidth }} px <br/>
      Canvas Height: {{ canvasHeight }} px <br/>
      Canvas Mouse X: {{ canvasMouse.x }} px <br/>
      Canvas Mouse Y: {{ canvasMouse.y }} px<br/>
      Selected FaceIds: {{ selectedFaces }}<br/>
      Hovered Face Id: {{ hoveredFace }}<br/>
    </div>
  </section>
</template>

<script>

import { THREE } from '@/three/three.js'
import { Colors, Materials } from '@/three/colors.js'
import { threeHelpers } from '@/three/three_helpers.js'
import { GeometryFromExport } from '@/three/loader.js'

export default {
  name: 'Canvas',
  mixins: [threeHelpers],
  props: [

  ],
  static () {
    return {
      // Non-reactive objects for performance
      faces: [],
      meshCube: null,
      camera: {},
      camStartData: {},
      controls: {},
      scene: new THREE.Scene(),
      renderer: new THREE.WebGLRenderer({ antialias: true }),
      raycaster: new THREE.Raycaster(),
    }
  },
  data () {
    return {
      // Reactive
      heuristics: true,
      selectedFaces: [], // Index
      unallowedFaces: [], // Index
      hoveredFace: null,
      mouse: new THREE.Vector2(),
      canvas: null, // dom
      canvasWidth: 0,
      canvasHeight: 0,
      canvasMouse: { x: 0, y: 0 },
      worldMouse: { x: 0, y: 0 },
    }
  },
  created () {
    window.addEventListener('resize', this.handleResize, false)
    window.addEventListener('mousemove', this.handleMouseMove, false)
    window.addEventListener('mousedown', this.handleMouseClick, false)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mousedown', this.handleMouseClick)
  },
  mounted () {
    this.canvas = document.getElementById('canvas')
    this.updateCavas()
    this.$nextTick(() => {
      this.initThree()
    })
  },
  methods: {
    initThree () {
      // Setup Renderer and add to Canvas
      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.setSize(this.canvasWidth, this.canvasHeight)
      this.canvas.appendChild(this.renderer.domElement)

      // Setup Camera
      this.setCamera()

      // Setup Scene
      this.scene.background = Colors.offWhite
      let ambientLight = new THREE.AmbientLight(0xffffff, 1)
      this.scene.add(ambientLight)

      // Setup Grid / Axis
      const gridHelper = new THREE.GridHelper(1000, 20, Colors.darkGray, Colors.darkGray)
      this.scene.add(gridHelper)
      const axesHelper = new THREE.AxesHelper(100) // X: red, Y: green, Z: blue
      this.scene.add(axesHelper)

      // Start Animation
      this.drawCube(500, 500, 500)
      this.animate()
    },
    animate () {
      requestAnimationFrame(this.animate)
      this.render()
    },
    render () {
      this.raycaster.setFromCamera(this.mouse, this.camera)
      this.setFaceColors()
      this.renderer.render(this.scene, this.camera)
    },
    updateCavas () {
      this.canvasWidth = document.getElementById('canvas').offsetWidth
      this.canvasHeight = document.getElementById('canvas').offsetHeight
    },
    setCamera () {
      this.camera = new THREE.PerspectiveCamera(45, this.canvasWidth / this.canvasHeight, 1, 10000)
      this.camera.position.set(1000, 1000, 1000)
      this.camera.lookAt(new THREE.Vector3(0, 0, 0))
      this.controls = new THREE.OrbitControls(this.camera)
      this.controls.enableDamping = true
      this.controls.dampingFactor = 1
      this.controls.rotateSpeed = 0.1
      this.controls.panSpeed = 0.1
      this.camStartData.position = this.camera.position.clone()
      this.camStartData.rotation = this.camera.rotation.clone()
      this.camStartData.controlCenter = this.controls.target.clone()
    },
    resetCamera () {
      this.controls.reset()
      this.camera.position.set(this.camStartData.position.x, this.camStartData.position.y, this.camStartData.position.z)
      this.camera.rotation.set(this.camStartData.rotation.x, this.camStartData.rotation.y, this.camStartData.rotation.z)
      this.controls.target.set(this.camStartData.controlCenter.x, this.camStartData.controlCenter.y, this.camStartData.controlCenter.z)
      this.controls.update()
    },
    handleResize () {
      this.updateCavas()
      if (this.camera) {
        this.camera.aspect = this.canvasWidth / this.canvasHeight
        this.camera.updateProjectionMatrix()
        this.renderer.setSize(this.canvasWidth, this.canvasHeight)
      }
    },
    handleMouseMove (e) {
      e.preventDefault()
      const offsetLeft = this.canvas.offsetLeft
      const offsetTop = this.canvas.offsetTop
      this.mouse.x = ((e.clientX - offsetLeft) / this.canvasWidth) * 2 - 1
      this.mouse.y = -((e.clientY - offsetTop) / this.canvasHeight) * 2 + 1
      this.canvasMouse.x = e.clientX - offsetLeft
      this.canvasMouse.y = e.clientY - offsetTop
    },
    handleMouseClick (e) {
      const intersections = this.getMouseIntersection([this.meshCube])
      const meshIntersections = intersections.filter(i => i.object.type === 'Mesh')
      if (meshIntersections.length === 0) return

      // Has Mesh Intersection
      const meshInt = meshIntersections[0]
      let faceIndex = meshInt.faceIndex
      let sibligFaceIndex = this.getSiblingFaceIndex(faceIndex)

      // Face is Selected or Sibling of Selected: toggle
      let isFaceSelected = this.selectedFaces.includes(faceIndex)
      let isSiblingOfFaceSelected = this.selectedFaces.includes(sibligFaceIndex)
      if (isFaceSelected) {
        this.selectedFaces = this.selectedFaces.filter(i => i !== faceIndex)
      } else if (isSiblingOfFaceSelected) {
        this.selectedFaces = this.selectedFaces.filter(i => i !== sibligFaceIndex)
      }
      if (isFaceSelected || isSiblingOfFaceSelected) {
        this.selectedFaces.splice(1, 2)
        this.clearPath()
        return
      }

      const shiftDown = e.shiftKey
      // Face is Unallowed or Sibling of unallowed: do nothing
      let isFaceUnallowed = this.unallowedFaces.includes(faceIndex)
      let isSiblingOfFaceUnallowed = this.unallowedFaces.includes(sibligFaceIndex)
      if ((!shiftDown) && (isFaceUnallowed || isSiblingOfFaceUnallowed)) return

      if (shiftDown) {
        // Face is Selected or Sibling of Selected: toggle
        let isFaceUnallowed = this.unallowedFaces.includes(faceIndex)
        let isSiblingOfFaceUnallowed = this.unallowedFaces.includes(sibligFaceIndex)
        if (isFaceUnallowed) {
          this.unallowedFaces = this.unallowedFaces.filter(i => i !== faceIndex)
        } else if (isSiblingOfFaceUnallowed) {
          this.unallowedFaces = this.unallowedFaces.filter(i => i !== sibligFaceIndex)
        } else {
          // ShiftIs Pressed
          this.unallowedFaces.push(faceIndex)
        }
        this.clearPath()
        return
      }

      // Already 2 SelectedL replace last
      if (this.selectedFaces.length >= 3) {
        this.selectedFaces.splice(1, 2)
        this.selectedFaces.push(faceIndex)
        this.clearPath()
      } else {
        // Less than 2, add one
        this.selectedFaces.push(faceIndex)
      }

      // Total selected is 2: run path
      if (this.selectedFaces.length === 2) {
        this.doVolumePath(this.meshCube, this.selectedFaces[0], this.selectedFaces[1])
      }
      if (this.selectedFaces.length === 3) {
        this.doVolumePath(this.meshCube, this.selectedFaces[1], this.selectedFaces[2])
      }
    },
    setFaceColors () {
      // Runs at every frame - use it to set face colors
      if (!this.meshCube) return // Cube not created yet
      const allFaces = this.meshCube.geometry.faces

      // Check for Hovered State
      const intersections = this.getMouseIntersection([this.meshCube])
      const hasIntersections = ((intersections !== undefined) && (intersections.length > 0))
      const hoveredFaceIndex = hasIntersections ? intersections[0].faceIndex : null

      // Set Color for all faces to white by default
      // apply hovered, selected, or unallowed color as needed
      for (let i = 0; i < allFaces.length; i++) {
        let faceIndex = i
        let face = allFaces[i]

        // Set Base Color
        let color = Colors.white

        // Hovered
        let isFaceHovered = hasIntersections ? (faceIndex === hoveredFaceIndex) : false
        let isSiblingOfHoveredFace = hasIntersections ? (faceIndex === this.getSiblingFaceIndex(hoveredFaceIndex)) : false

        // Selected
        let isFaceSelected = this.selectedFaces.includes(faceIndex)
        let isSiblingOfSelectedFace = this.selectedFaces.includes(this.getSiblingFaceIndex(faceIndex))

        // Unallowed
        let isFaceUnallowed = this.unallowedFaces.includes(faceIndex)
        let isSiblingOfFaceUnallowed = this.unallowedFaces.includes(this.getSiblingFaceIndex(faceIndex))

        // Set Color
        if (isFaceSelected || isSiblingOfSelectedFace) {
          color = Colors.abb
        } else if (isFaceHovered || isSiblingOfHoveredFace) {
          color = Colors.abbDesat
          this.hoveredFace = `${faceIndex} + ${this.getSiblingFaceIndex(faceIndex)}`
        } else if (isFaceUnallowed || isSiblingOfFaceUnallowed) {
          color = Colors.gray
        }
        face.color = color
      }
      this.meshCube.geometry.elementsNeedUpdate = true
    },
    getMouseIntersection (geometries) {
      const geomIntersections = this.raycaster.intersectObjects(geometries)
      if (geomIntersections != null) {
        return geomIntersections
      }
    },
    drawCube (xUnit, yUnit, zUnit) {
      // Create Box Mesh - GeometryFromExport uses Geometry generated by Rhino + Grasshopper
      let geometry = GeometryFromExport()
      let meshCube = this.makeMesh(geometry)
      // Manually Draw Cubes
      // const meshCube = this.makeCube(xUnit, yUnit, zUnit, { x: 250, y: 250, z: -250 })

      this.scene.add(meshCube)
      this.meshCube = meshCube

      // Add Edges
      const edges = this.makeEdge(meshCube.geometry)
      this.scene.add(edges)

      // Add WireFrame
      const lineSegments = this.makeWireframe(meshCube.geometry)
      this.scene.add(lineSegments)
    },
    doVolumePath (mesh, face1, face2) {
      // This is the the main path-finding algorithm
      const allVertices = mesh.geometry.vertices

      // Unallowed Vertices
      const unallowedFaces = this.unallowedFaces
        .concat(
          this.unallowedFaces.map(f => this.getSiblingFaceIndex(f))
        ).map(f => mesh.geometry.faces[f])
      const unallowedVertices = unallowedFaces.map(
        f => [allVertices[f.a]]
          .concat(allVertices[f.b])
          .concat(allVertices[f.c])
      ).flat()

      // Get Faces + Sibling Faces
      const face1a = mesh.geometry.faces[face1]
      const face1b = mesh.geometry.faces[this.getSiblingFaceIndex(face1)]
      const face2a = mesh.geometry.faces[face2]
      const face2b = mesh.geometry.faces[this.getSiblingFaceIndex(face2)]

      // Combines Vertices from two sibling triangles, de-duplicated
      const face1aPoints = this.pointsFromFace(face1a, mesh.geometry.vertices)
      const face1bPoints = this.pointsFromFace(face1b, mesh.geometry.vertices)
      const verticesA = Array.from(new Set(face1aPoints.concat(face1bPoints)))

      const face2aPoints = this.pointsFromFace(face2a, mesh.geometry.vertices)
      const face2bPoints = this.pointsFromFace(face2b, mesh.geometry.vertices)
      const verticesB = Array.from(new Set(face2aPoints.concat(face2bPoints)))

      // Get Center of Vertices - This will be the Starting Poiunts
      const centerA = this.getCentroid(...verticesA)
      const centerB = this.getCentroid(...verticesB)

      let startPoint = centerA
      let targetPoint = centerB

      console.log('Start Point is: ' + JSON.stringify(startPoint))
      console.log('Target Point is: ' + JSON.stringify(targetPoint))

      // Angle Heuristic - iteratively calculate sum of all angles turned by up to given node
      const totalAngleCost = (node) => {
        let angle = 0
        while (node.parent && node.parent.parent) {
          const a = this.getAngleBetweenThreePoints(node, node.parent, node.parent.parent)
          angle += a
          node = node.parent
        }
        return angle
      }
      totalAngleCost(startPoint)

      // A* Functions /
      // Helper Functions
      const distToEnd = (node) => node.manhattanDistanceTo(targetPoint)
      const distToStart = (node) => node.manhattanDistanceTo(startPoint)

      // Weight: 0: Dijkstra's, Higher = A*
      const hWeight = this.heuristics ? 0.75 : 0
      // H: Heuristic (Distance to end, helps guide it towards target)
      const h = (node) => (distToEnd(node) * hWeight)
      // G: Distance Start
      const g = (node) => distToStart(node) + totalAngleCost(node)
      // F: Total Score
      const f = (node) => g(node) + h(node)

      const CELL_SIZE = 50 // TODO: make configurable - must match mesh cell size
      let isSearching = true
      let iterations = 0
      let openList = [startPoint]
      let closedList = []
      let currentNode
      while ((openList.length > 0) || isSearching) {
        // Circuit Breaker - prevent infinite loops
        if (iterations > allVertices.length) {
          console.warn('exceeded allowed iterations')
          this.clearPath()
          break
        }

        // Set currentNode to node with lowest Score in openList
        currentNode = openList.sort((a, b) => f(a) - f(b))[0]

        // Check if completed
        if (currentNode.distanceTo(targetPoint) <= CELL_SIZE) {
          isSearching = false
          closedList.push(currentNode)
          break
        }

        // Move node to closed list
        closedList.push(currentNode)
        // Remove current from open list
        openList = openList.filter(n => n !== currentNode)

        // Get 4 Adjacent Vertices // TODO Remove
        let neighbors = allVertices.filter(v => v.distanceTo(currentNode) === CELL_SIZE)

        // 1st Iteration: starting from centroid of face
        if (neighbors.length === 0) {
          neighbors = allVertices.filter(v => v.distanceTo(currentNode) < CELL_SIZE)
        }

        // Remove Vertices in Unallowed list
        let allowedneighbors = neighbors
          .filter(pt => !unallowedVertices.includes(pt)) // Exclude Obstructions
          .filter(pt => !closedList.includes(pt)) // Exclude nodes in closed list

        // Calculate here once before iterating through neighbors
        const gCurrentNode = g(currentNode)

        for (let neighbor of allowedneighbors) {
          const gNeighbor = gCurrentNode + currentNode.distanceTo(neighbor)

          const nodeInOpenList = openList.includes(neighbor)
          if (!nodeInOpenList) {
            openList.push(neighbor)
          } else if (gNeighbor >= gCurrentNode) {
            continue
          }
          neighbor.parent = currentNode
        }

        // Track iterations for debugging and circuit breaking
        iterations++
      }
      // Draw Solution
      this.animatePath(currentNode, closedList, openList)
    },
    animatePath: async function (currentNode, closedList) {
      const TIME = 20
      let path = []

      // Collect path by retrieving parent attribute recursively
      while (true) {
        if (!currentNode) break
        path.push(currentNode)
        currentNode = currentNode.parent
      }

      // Remove first, draw closedList (explored nodes)
      closedList.shift()
      for (let node of closedList) {
        await this.sleep(TIME)
        let sphere = this.makeSphere(4, node, Materials.darkGray)
        sphere.userData.sphereClosedPath = true
        this.scene.add(sphere)
      }

      // Remove Last, draw solution
      path.pop()
      path = path.reverse()
      for (let node of path) {
        await this.sleep(TIME)
        let sphere = this.makeSphere(5, node, Materials.abb)
        sphere.userData.spherePath = true
        this.scene.add(sphere)
      }
      // Remove closedList
      await this.sleep(500)
      this.scene.children.filter(c => c.userData.sphereClosedPath).map(o => this.scene.remove(o))
    },
    sleep (ms) {
      // Helper sleep function
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    dissalowCeiling () {
      // Set all faces with world Z-up normal to disallowed
      const allFaces = this.meshCube.geometry.faces
      for (let i = 0; i < allFaces.length; i++) {
        if (i % 2 === 0) continue // skip every other since it's handled by sibling relationhsips
        let face = this.meshCube.geometry.faces[i]
        if (face.normal.y === 1) this.unallowedFaces.push(i)
      }
    },
    clearPath () {
      this.scene.children.filter(c => c.userData.spherePath || c.userData.sphereClosedPath).map(o => this.scene.remove(o))
    },
    clearSelection () {
      this.clearPath()
      this.selectedFaces = []
      this.unallowedFaces = []
    },
  },
  computed: {
    debugMode () {
      // Add ?debug=1 to url to show debugging panel
      return Boolean(this.$route.query.debug)
    },
  }
}
</script>

<style lang="sass">

#canvas
  margin: 0
  padding: 0px
  background-color: #fafafa
  width: 100%
  top: 52px
  height: calc(100vh - 52px)
  position: absolute

#action-panel
  position: absolute
  right: 30px
  bottom: 30px
  .button, p
    margin: 5px

#info-panel
  position: absolute
  bottom: 30px
  left: 30px

.white-panel
  border: 1px solid #ddd
  border-radius: 10px
  background-color: white
  opacity: 0.9
  color: #333
  padding: 8px 16px
  font-size: 0.7rem

</style>
